import type { UserContext } from '../context/user-context.js'
import type { AttachmentService } from './attachment.svelte.js'
import type { ClassroomService } from './classroom.svelte.js'
import type { ChildService } from './child.svelte.js'
import type { FeatureService } from './feature.svelte.js'
import type { GuardianService } from './guardian.svelte.js'
import type { LocalEncryptionService } from './local-encryption.svelte.js'
import type { MessagingService } from './messaging/service.svelte.js'
import type { NotificationSettingsService } from './notification-settings.js'
import { CacheWritingService } from './persistence/cache-writing-service.js'

export interface UserScopedServiceGraph {
    readonly attachment: AttachmentService
    readonly classroom: ClassroomService
    readonly child: ChildService
    readonly context: UserContext
    readonly feature: FeatureService
    readonly guardian: GuardianService
    readonly localEncryption: LocalEncryptionService
    readonly messaging: MessagingService
    readonly notificationSettings: NotificationSettingsService
}

export interface UserScopedServiceScope {
    readonly context: UserContext
    resolveServices(): UserScopedServiceGraph
}

export type CurrentUserScopedServices<
    Key extends keyof UserScopedServiceGraph = keyof UserScopedServiceGraph
> = {
    readonly currentUserServices: Pick<UserScopedServiceGraph, Key> | undefined
}

export abstract class UserScopedService extends CacheWritingService {
    readonly #scope: UserScopedServiceScope

    protected constructor(scope: UserScopedServiceScope) {
        super()
        this.#scope = scope
    }

    protected get userContext(): UserContext {
        return this.#scope.context
    }

    protected get userServices(): UserScopedServiceGraph {
        return this.#scope.resolveServices()
    }
}
